// Synthetic CSC load profile — builds the SAME { stats, charts:{daily,hourly,monthly} }
// shape that analyzeLoadProfile() (a real TNB upload) produces, but from the calculator's
// own inputs (system kWp, monthly usage, operating days/hours, night baseline, mode). This
// lets the HTML-style CscLoadProfileChart render even when no interval Excel was uploaded —
// "same chart, just synthetic data". (user 2026-07-24)
//
// The hourly load model mirrors cscEnergyChart / the saving calc:
//   - operating day: load only inside operating hours; op∩solar hours carry the daytime
//     share, off-solar op hours carry the evening share; nothing before opening.
//   - non-operating day: a flat ~20% standby across 24h.
// Solar uses the shared empirical curve (solarKwhInHour, 07:00–20:00). Day/night IMPORT is
// classified 08:00–18:00 day vs 18:00–08:00 night (to match the TNB tariff windows).
// ATAP exports the hourly surplus; SELCo curtails it (zero export).
import { solarKwhInHour, CSC_YIELD_PER_KWP, CSC_WORKWEEKS_PER_MONTH } from '../hooks/useCscBillProposal';

const NON_OP_SHARE = 0.20;   // a closed day draws ~20% of an operating day (matches cscEnergyChart)
const DAY_START = 8, DAY_END = 18;

const median = (arr) => {
    if (!arr.length) return 0;
    const a = [...arr].sort((x, y) => x - y);
    const m = Math.floor(a.length / 2);
    return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
};
const r1 = (v) => Math.round(v * 10) / 10;

// One 24-hour day of gross {load, solar} per hour for the given day type. A small
// standby load runs ALL 24h; the rest sits flat across operating hours — this gives a
// realistic profile with no single-hour spikes (the old model piled the whole evening
// share into 1–2 off-solar op-hours, spiking them). (user 2026-07-25)
function dayHours({ kwp, openDayUsage, opStart, opEnd, baseline, dayType, battUsable = 0, loadShape = null }) {
    const opHours = Math.max(1, opEnd - opStart);
    const stbyHourly = baseline * openDayUsage / 24;                                   // 24/7 standby
    const activeHourly = Math.max(0, openDayUsage - stbyHourly * 24) / opHours;        // operating-hour load
    // When an uploaded profile supplies a real 24h shape (fractions summing to 1), distribute
    // the day's total usage by that shape; otherwise use the flat synthetic model. A meter
    // WITHOUT its own profile adapts a sibling's pattern this way. (user 2026-07-25)
    const dayTotal = dayType === 'operating' ? openDayUsage : NON_OP_SHARE * openDayUsage;

    // Per-hour load + generation.
    const load = [], gen = [];
    for (let h = 0; h < 24; h++) {
        const inOp = h >= opStart && h < opEnd;
        load[h] = loadShape
            ? dayTotal * (loadShape[h] || 0)
            : (dayType === 'operating' ? (inOp ? stbyHourly + activeHourly : stbyHourly) : NON_OP_SHARE * openDayUsage / 24);
        gen[h] = solarKwhInHour(kwp, h);
    }

    // Battery: 2-pass state-of-charge sim (yesterday's charge carries into this morning).
    // Midday surplus CHARGES the battery (up to remaining capacity); a load deficit
    // DISCHARGES it — so stored solar covers the morning/evening ramp instead of exporting
    // it at RM0.20. Only surplus the battery can't hold spills over as export. (user 2026-07-25)
    const battOn = battUsable > 0;
    let soc = battUsable;
    const charge = new Array(24).fill(0), discharge = new Array(24).fill(0);
    for (let pass = 0; pass < 2; pass++) {
        for (let h = 0; h < 24; h++) {
            const surplus = Math.max(0, gen[h] - load[h]);
            const deficit = Math.max(0, load[h] - gen[h]);
            let toBatt = 0, fromBatt = 0;
            if (battOn && surplus > 0) { toBatt = Math.min(battUsable - soc, surplus); soc += toBatt; }
            if (battOn && deficit > 0) { fromBatt = Math.min(soc, deficit); soc -= fromBatt; }
            if (pass === 1) { charge[h] = toBatt; discharge[h] = fromBatt; }
        }
    }

    const rows = [];
    for (let h = 0; h < 24; h++) {
        const self = Math.min(gen[h], load[h]);          // solar used on-site directly
        const surplus = Math.max(0, gen[h] - load[h]);
        const isDay = h >= DAY_START && h < DAY_END;
        rows.push({
            label: `${String(h).padStart(2, '0')}:00`,
            // The orange candle = solar USED on-site directly. Surplus splits into
            // battery-charge + export so nothing double-counts. self + charge + export = gen.
            solar: r1(self),
            solarGen: r1(gen[h]),                        // total generation (for the stat card)
            dayImport: isDay ? r1(load[h]) : 0,          // GROSS usage before solar
            nightImport: isDay ? 0 : r1(load[h]),
            battCharge: r1(charge[h]),                   // solar → battery (stored, not exported)
            battDischarge: r1(discharge[h]),             // battery → load (covers the ramp)
            // Only what the battery couldn't store exports (ATAP) / curtails (SELCo).
            export: r1(Math.max(0, surplus - charge[h])),
        });
    }
    return rows;
}

const sum = (rows, key) => rows.reduce((s, r) => s + (r[key] || 0), 0);

// Normalized 24h load shape (fractions summing to 1) from an uploaded profile's hourly
// chart — usage[h] = dayImport + nightImport. Lets a meter WITHOUT its own profile adapt a
// sibling's real pattern, scaled to its own usage. (user 2026-07-25)
export function loadShapeFromAnalysis(analysis) {
    const hourly = analysis && analysis.charts && analysis.charts.hourly;
    if (!hourly || hourly.length !== 24) return null;
    const usage = hourly.map((h) => (h.dayImport || 0) + (h.nightImport || 0));
    const total = usage.reduce((s, x) => s + x, 0);
    if (total <= 0) return null;
    return usage.map((u) => u / total);
}

export function buildSyntheticAnalysis({ kwp, monthlyKwh, operatingDays = 7, opStart = 9, opEnd = 18, offHourBaselinePct = 0.2, mode = 'atap', batteryKwh = 0, loadShape = null }) {
    if (!kwp || !monthlyKwh) return null;
    const workdays = Math.max(0, operatingDays) * CSC_WORKWEEKS_PER_MONTH;
    const offdays = Math.max(0, 7 - operatingDays) * CSC_WORKWEEKS_PER_MONTH;
    const baseline = Math.min(0.5, Math.max(0, offHourBaselinePct));
    const openDayUsage = (workdays + NON_OP_SHARE * offdays) > 0 ? monthlyKwh / (workdays + NON_OP_SHARE * offdays) : 0;
    const dailyGen = kwp * CSC_YIELD_PER_KWP;
    const battUsable = (Number(batteryKwh) || 0) * 0.9 * 0.9;   // RTE × DOD, matches the saving calc
    const hasBatt = battUsable > 0;

    const opRows = dayHours({ kwp, openDayUsage, opStart, opEnd, baseline, dayType: 'operating', battUsable, loadShape });
    const offRows = dayHours({ kwp, openDayUsage, opStart, opEnd, baseline, dayType: 'nonOperating', battUsable, loadShape });

    // Per-day totals for each day type. Surplus = sum of each hour's solar-beyond-load
    // (per-hour, so a big-solar hour shows its real excess). ATAP exports it (RM0.20 SMP);
    // SELCo curtails it (shown as kWh but NOT counted). (user 2026-07-25)
    // solar = self-used (sum of per-hour self); solarGen = total generation.
    const dayTotals = (rows) => ({
        dayImport: sum(rows, 'dayImport'), nightImport: sum(rows, 'nightImport'),
        solar: sum(rows, 'solar'), solarGen: dailyGen, export: sum(rows, 'export'),
        battCharge: sum(rows, 'battCharge'), battDischarge: sum(rows, 'battDischarge'),
    });
    const opDay = dayTotals(opRows);
    const offDay = dayTotals(offRows);

    // Daily chart — 4 representative weeks (28 bars): operatingDays operating + rest closed.
    const daily = [];
    for (let w = 0; w < 4; w++) {
        for (let d = 0; d < 7; d++) {
            const isOp = d < operatingDays;
            const src = isOp ? opDay : offDay;
            daily.push({
                label: `W${w + 1}·${['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'][d]}`,
                dayImport: +src.dayImport.toFixed(1), nightImport: +src.nightImport.toFixed(1),
                solar: +src.solar.toFixed(1), solarGen: +src.solarGen.toFixed(1), export: +src.export.toFixed(1),
                battCharge: +src.battCharge.toFixed(1), battDischarge: +src.battDischarge.toFixed(1),
            });
        }
    }

    // Hourly chart — the representative operating day.
    const hourly = opRows.map((r) => ({ label: r.label, dayImport: r.dayImport, nightImport: r.nightImport, solar: r.solar, solarGen: r.solarGen, export: r.export, battCharge: r.battCharge, battDischarge: r.battDischarge }));

    // Monthly chart — one bar, the month's totals.
    const monthly = [{
        label: 'This month',
        dayImport: +(workdays * opDay.dayImport + offdays * offDay.dayImport).toFixed(0),
        nightImport: +(workdays * opDay.nightImport + offdays * offDay.nightImport).toFixed(0),
        solar: +(workdays * opDay.solar + offdays * offDay.solar).toFixed(0),
        solarGen: +(dailyGen * (workdays + offdays)).toFixed(0),
        export: +(workdays * opDay.export + offdays * offDay.export).toFixed(0),
        battCharge: +(workdays * opDay.battCharge + offdays * offDay.battCharge).toFixed(0),
        battDischarge: +(workdays * opDay.battDischarge + offdays * offDay.battDischarge).toFixed(0),
    }];

    const stats = {
        kwp, nDays: Math.round(workdays + offdays), solarPerDay: dailyGen,
        dayImportMedian: median(daily.map((d) => d.dayImport)),
        nightImportMedian: median(daily.map((d) => d.nightImport)),
        exportMedian: median(daily.map((d) => d.export)),
    };

    const flags = [];
    if (hasBatt) flags.push({ type: 'battery', label: 'Battery — midday surplus is stored (teal) and discharged to the evening/night load, instead of exporting it at RM 0.20.' });
    if (mode === 'selco') flags.push({ type: 'selco', label: 'SELCo mode — surplus solar is curtailed (wasted, zero export credit).' });
    else flags.push({ type: 'atap', label: 'ATAP mode — daytime usage draws from solar first; surplus exported to grid at RM 0.20/kWh (SMP).' });

    return { stats, flags, charts: { daily, hourly, monthly }, synthetic: true, mode, hasBatt };
}
